import React, { FormEvent, ReactElement, useCallback, useEffect, useState } from 'react'
import {
  Button,
  ButtonType,
  Form,
  formatPhoneNumberIntl,
  FormGroup,
  Input,
  InputType,
  Select,
  SubmitButton
} from '@digicert/dcone-common-ui'
import { connect } from 'react-redux'
import { IAppState } from '../../../core/store/store'
import { Translate } from 'react-localize-redux'
import { editUser } from '../../../core/actions/Users.actions'
import { fetchProfile } from '../../../core/actions/Profile.actions'
import { initLocalization } from '../../../core/actions/Localization.actions'
import history from '../../../core/history/history'
import { IEditProfileProps } from './EditProfile.types'
import { IAccessScope } from '../../../reducers/Reducers.interfaces'
import Cookies from 'js-cookie'
import { ACCOUNTS_LOCALE_COOKIE_NAME } from '../../../core/utils/constants'
import { BasePathConst } from '../../../configs/Path.configs'
import { isValidEmail, translate } from '../../../shared/helpers/utils'
import { checkIfUsernameExists } from 'public-pages/self-service-user/SelfServiceUser.actions'
import styles from './EditProfile.module.scss'

const EditProfile = (
  {
    fetchProfile,
    checkIfUsernameExists,
    localeList,
    profile,
    editUser,
    initLocalization
  }: IEditProfileProps): ReactElement => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [userName, setUserName] = useState('')
  const [samlUserName, setSamlUserName] = useState('')
  const [oidcUserName, setOidcUserName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [otherPhoneNumber, setOtherPhoneNumber] = useState('')
  const [language, setLanguage] = useState('')
  const [nameDuplicateError, setNameDuplicateError] = useState(false)

  const changeFirstName = useCallback(
    (event) => setFirstName(event.target.value),
    [setFirstName]
  )

  const changeLastName = useCallback(
    (event) => setLastName(event.target.value),
    [setLastName]
  )

  const changeEmail = useCallback(
    (event) => setEmail(event.target.value),
    [setEmail]
  )

  const validateEmail = ():string => {
    if(!isValidEmail(email)){
      return translate('manageUser.administrators.validation.invalidEmail') as string
    }
    return ''
  }

  const validateUserName = (): any => {
    if (nameDuplicateError) {
        return translate('notifications.exceptions.USERNAME_EXISTS') as string;
      }
  }

  const changeUserName = useCallback(
    (event) => {
      setUserName(event.target.value)
      checkIfUsernameExists(event.target.value, undefined, 'userName', profile!.id).then((res:boolean) => {
        if (res) {
            setNameDuplicateError(true)
        } else {
            setNameDuplicateError(false)
        }
    })
    },
    [setUserName]
  )

  const changeSamlUserName = useCallback(
    (event) => setSamlUserName(event.target.value),
    [setSamlUserName]
  )

  const changeOidcUserName = useCallback(
      (event) => setOidcUserName(event.target.value),
      [setOidcUserName]
  )

  const changePhoneNumber = useCallback(
    (value) => {
      if (value) {
        setPhoneNumber(value);
      }
    },
    [setPhoneNumber]
  )

  const onOtherPhoneChange = (event) => {
    setOtherPhoneNumber(event.target.value)
  }

  const changeLanguage = (value): void => {
    setLanguage(value)
  };

  const getLocaleOptions = () => {
    return localeList.locales.map((locale) => {
      return {
        label: locale.name,
        value: locale.id
      }
    })
  }

  const onCancel = (event) => {
    event.preventDefault()
    history.goBack()
  }

  const updateProfile = async (event:FormEvent): Promise<void> => {
    event.preventDefault()

    if(profile) {
      const profileData = {
        first_name: firstName,
        last_name: lastName,
        user_name: userName,
        email: email,
        phone: formatPhoneNumberIntl(phoneNumber),
        other_phone: otherPhoneNumber,
        locale: language,
        permissions: profile.permissions,
        accounts: profile.access_scope === IAccessScope.account ? profile.accounts.filter(account => account.active) : [],
        access_scope: profile.access_scope === IAccessScope.system,
        "group_ids": profile.groups.map(group => {return group.id})
      }

      await editUser(profile.id, profileData, false)
      Cookies.set(ACCOUNTS_LOCALE_COOKIE_NAME, profileData.locale, { secure: true })
      await initLocalization(profileData.locale).then(() => {
        window.location.href = `${BasePathConst}/profile`
      })
    }
  }

  useEffect(() => {
    fetchProfile().then((response: any) => {
      setFirstName(response.first_name)
      setLastName(response.last_name)
      setUserName(response.user_name)
      setEmail(response.email)
      // phone number maybe optional
      setPhoneNumber(response.phone || '')
      setOtherPhoneNumber(response.other_phone || '')
      setLanguage(response.locale)
      setSamlUserName(response.saml_username)
      setOidcUserName(response.oidc_username)
    })
  }, [])

  return (
    <section className={styles.editProfile}>
      <h1><Translate id='profile.title'/></h1>

      <Form onSubmit={updateProfile}>
        <div className={styles.formElementContainer}>
          <FormGroup>
            <Input
              label={<Translate id='common.form.firstName'/>}
              required
              value={firstName}
              errors={{ valueMissing: <Translate id='manageUser.administrators.validation.required'/> }}
              onChange={changeFirstName}
            />

            <Input
              label={<Translate id='common.form.lnOptional'/>}
              value={lastName}
              onChange={changeLastName}
            />
          </FormGroup>

          <FormGroup>
            <Input
              label={<Translate id='common.form.stdUsername'/>}
              required
              value={userName}
              errors={{ valueMissing: <Translate id='manageUser.administrators.validation.required'/> }}
              onChange={changeUserName}
              validate={validateUserName}
            />
          </FormGroup>

          {samlUserName && (
            <FormGroup>
              <Input
                label={<Translate id='common.form.samlUsername'/>}
                required
                value={samlUserName}
                errors={{ valueMissing: <Translate id='manageUser.administrators.validation.required'/> }}
                onChange={changeSamlUserName}
                readOnly
              />
            </FormGroup>
          )}

          {oidcUserName && (
            <FormGroup>
              <Input
                label={<Translate id='common.form.oidcUsername'/>}
                required
                value={oidcUserName}
                errors={{ valueMissing: <Translate id='manageUser.administrators.validation.required'/> }}
                onChange={changeOidcUserName}
                readOnly
              />
            </FormGroup>
          )}

          <FormGroup>
            <Input
              label={<Translate id='profile.email'/>}
              type={InputType.EMAIL}
              value={email}
              onChange={changeEmail}
              validate={validateEmail}
            />
          </FormGroup>

          <FormGroup>
            <Input
              label={<Translate id='profile.phone'/>}
              type={InputType.TELEPHONE}
              value={phoneNumber}
              errors={{ valueMissing: <Translate id='manageUser.administrators.validation.required'/> }}
              onChange={changePhoneNumber}
            />
          </FormGroup>

          <FormGroup>
            <Input
              label={<Translate id='common.form.otherPhoneOptional'/>}
              value={otherPhoneNumber}
              onChange={onOtherPhoneChange}
            />
          </FormGroup>

          <FormGroup>
            <Select
              getPopupContainer={triggerNode => triggerNode.parentElement}
              label={<Translate id='profile.language'/>}
              options={getLocaleOptions()}
              defaultValue={language}
              value={language}
              errors={{ valueMissing: <Translate id='manageUser.administrators.validation.required'/> }}
              onChange={changeLanguage}
            />
          </FormGroup>
        </div>

        <div className={styles.formActions}>
          <Button buttonType={ButtonType.SECONDARY} onClick={onCancel} id='cancel_btn_profile'>
            <Translate id='profile.cancelBtn'/>
          </Button>

          <SubmitButton id='save_or_update_contact_btn_profile'>
            <Translate id='profile.updateBtn'/>
          </SubmitButton>
        </div>
      </Form>
    </section>
  )
}

export default connect(
  (state: IAppState) => ({
    profile: state.profile.data,
    isEditing: state.users.isEditing,
    localeList: state.locales
  }),
  { editUser, fetchProfile, initLocalization, checkIfUsernameExists }
)(EditProfile)
